import { useRef, useState, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import useColumnState from '../../../hooks/useColumnState';
import {
    HStack,
    IconButton,
    Text,
    Tooltip,
    useToast,
    AlertTitle,
} from '@chakra-ui/react';
import { Alert, AlertIcon } from '@/components/v4';
import AGGridMain from '../../../components/tables/AGGrid/AGGridMain';
import InvoiceLines from './invoiceLines/Index';
import config from '../../../config.json';
import { axios } from '../../../services/axios';
import ModalApprovalContent from './ModalApprovalContent';
import FieldsDrawer from './FieldsDrawer';
import { useQuery } from 'hooks/useCustomQuery';
import { NegativeSixDP } from 'components/maintenance/NumberFormatCustom';
import { CustomSelect, Option } from 'components/v4/CustomSelect';
import { EditIcon } from '@chakra-ui/icons';
import { MdDownload, MdLoop, MdList } from 'react-icons/md';
import { BsThreeDotsVertical } from 'react-icons/bs';
import ActionMenu from 'components/presentational/ActionMenu';
import TimeCellRenderer from 'components/tables/AGGrid/CustomCells/TimeCellRenderer';

/*
 * Base component for rendering the Invoices tab on the sidebar
 */

const Invoice = () => {
    const dispatch = useDispatch();
    const tableRef = useRef();
    // Local state for modal and child view
    const [child, setChild] = useState(null);
    const [modal, setModal] = useState({
        show: false,
        mode: '',
        data: null,
        state: null,
    });
    // Remove xeroInfo and cdrInfo state, use local handlers instead

    // Toast for local feedback
    const toast = useToast();
    const invoiceType = useSelector((state) => state.table.invoiceType);
    const invoiceTypeRef = useRef(invoiceType);
    useEffect(() => {
        invoiceTypeRef.current = invoiceType;
    }, [invoiceType]);
    const { currentScope, minimumScope, requiredScope } = useSelector(
        (state) => ({
            ...state.login,
        }),
    );
    const currentScopeRef = useRef(currentScope);
    useEffect(() => {
        currentScopeRef.current = currentScope;
    }, [currentScope]);
    const { currentCompany, currentPartner, currentWholesaler } = useSelector(
        (state) => ({ ...state.navigationLists }),
    );
    const { darkMode } = useSelector((state) => state.settings);

    // Track previous scope to detect changes
    const prevScopeRef = useRef(null);

    // When scope changes (from navbar), update filter (invoice type) to match
    useEffect(() => {
        // Run on initial mount or when scope actually changed
        if (
            prevScopeRef.current === null ||
            prevScopeRef.current !== currentScope
        ) {
            let newType;
            if (currentScope === 80 || currentScope === 60) {
                newType = 2; // Wholesaler
            } else if (currentScope === 40) {
                newType = 1; // Partner
            } else if (currentScope === 20) {
                newType = 0; // Company
            }
            if (newType !== undefined) {
                dispatch({
                    type: 'CHANGE_TABLE_INVOICE_TYPE',
                    payload: newType,
                });
            }
            prevScopeRef.current = currentScope;
        }
    }, [currentScope, dispatch]);

    // Utility to download a single row as CSV
    // function downloadCSV(filename, rowObj) {
    //     const replacer = (key, value) =>
    //         value === null || value === undefined ? '' : value;
    //     const header = Object.keys(rowObj);
    //     const csv = [
    //         header.join(','),
    //         header
    //             .map((fieldName) => JSON.stringify(rowObj[fieldName], replacer))
    //             .join(','),
    //     ].join('\r\n');
    //     const blob = new Blob([csv], { type: 'text/csv' });
    //     const link = document.createElement('a');
    //     link.href = URL.createObjectURL(blob);
    //     link.download = filename;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // }

    // Utility to download CDR CSV from API (replicating previous async Redux pattern, but local)
    async function downloadCDR(filename, invoiceId) {
        try {
            // Call API endpoint: invoice/{invoiceId}/export/cdr as a file download
            const res = await axios.get(`/invoice/${invoiceId}/export/cdr`, {
                responseType: 'blob',
            });
            // Try to get filename from Content-Disposition header
            let contentDisposition =
                res.headers && res.headers['content-disposition'];
            let suggestedFilename = filename;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^";]+)"?/);
                if (match && match[1]) {
                    suggestedFilename = match[1];
                }
            }
            // Download the blob as a file
            const blob = new Blob([res.data], {
                type: res.data.type || 'text/csv',
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = suggestedFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast({
                title: 'CDR Download',
                description: 'CDR CSV downloaded.',
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'top-right',
            });
        } catch (err) {
            toast({
                title: 'CDR Download',
                description: 'Failed to download CDR.',
                status: 'error',
                duration: 2000,
                isClosable: true,
                position: 'top-right',
            });
        }
    }

    // Utility to download Xero export from API
    async function downloadXero(filename, invoiceId) {
        try {
            // Call API endpoint: invoice/{invoiceId}/export/xero as a file download
            const res = await axios.get(`/invoice/${invoiceId}/export/xero`, {
                responseType: 'blob',
            });
            // Try to get filename from Content-Disposition header
            let contentDisposition =
                res.headers && res.headers['content-disposition'];
            let suggestedFilename = filename;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^";]+)"?/);
                if (match && match[1]) {
                    suggestedFilename = match[1];
                }
            }
            // Download the blob as a file
            const blob = new Blob([res.data], {
                type: res.data.type || 'text/csv',
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = suggestedFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast({
                title: 'Export Complete',
                description: 'Export downloaded successfully.',
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'top-right',
            });
        } catch (err) {
            toast({
                title: 'Export Failed',
                description: 'Failed to download export.',
                status: 'error',
                duration: 2000,
                isClosable: true,
                position: 'top-right',
            });
        }
    }

    // Standalone ActionsRenderer function, matching InternationalRatesList robust pattern
    function ActionsRenderer(params) {
        const rowData = params.data;
        if (!rowData) return null;
        const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));
        const approvalHidden =
            typeof rowData.invoiceStatus !== 'undefined' &&
            (rowData.invoiceStatus === 8 || rowData.invoiceStatus === 15);

        // Local handlers for export and cdr
        // const handleExport = () => {
        //     toast({
        //         title: 'Export',
        //         description: `Exporting invoice ${rowData.invoiceNumber} (CSV download)`,
        //         status: 'info',
        //         duration: 2000,
        //         isClosable: true,
        //         position: 'top-right',
        //     });
        //     downloadCSV(`invoice_${rowData.invoiceNumber}.csv`, rowData);
        // };
        const handleCDR = async () => {
            toast({
                title: 'CDR Download',
                description: `Downloading CDR for invoice ${rowData.invoiceNumber}...`,
                status: 'info',
                duration: 2000,
                isClosable: true,
                position: 'top-right',
            });
            await downloadCDR(
                `cdr_${rowData.invoiceNumber}.csv`,
                rowData.invoiceId || rowData.id || rowData.invoiceNumber,
            );
        };
        const handleXero = async () => {
            await downloadXero(
                `Invoice_${rowData.invoiceNumber}.csv`,
                rowData.invoiceId || rowData.id || rowData.invoiceNumber,
            );
        };
        // Local handlers for Edit and Status (Approval)
        const handleEdit = () => {
            setModal({
                show: true,
                mode: 'Edit',
                data: deepCopy(rowData),
                state: { ...rowData },
            });
        };
        const handleStatus = () => {
            const statusArr =
                config.invoices.statusUpload &&
                config.invoices.statusUpload[rowData?.invoiceStatus];
            const firstStatus =
                Array.isArray(statusArr) && statusArr.length > 0
                    ? statusArr[0]
                    : undefined;
            let statusObj = undefined;
            if (firstStatus && typeof firstStatus === 'object') {
                statusObj = { ...firstStatus };
            } else if (firstStatus) {
                statusObj = { value: firstStatus };
            }
            setModal({
                show: true,
                mode: 'Approval',
                data: deepCopy(rowData),
                state: statusObj,
            });
        };
        const handleViewLines = () => {
            setChild(deepCopy(rowData));
        };

        // Build actionItems array
        const actionItems = [
            {
                label: 'Edit',
                onClick: handleEdit,
                icon: EditIcon,
            },
            {
                label: 'View Invoice Lines',
                onClick: handleViewLines,
                icon: MdList,
            },
            // {
            //     label: 'Export CSV',
            //     onClick: handleExport,
            //     icon: MdDownload,
            // },
            {
                label: 'Export CSV',
                onClick: handleXero,
                icon: MdDownload,
            },
            ...(currentScopeRef.current === 80 || invoiceTypeRef.current === 0
                ? [
                      {
                          label: 'Export CDR',
                          onClick: handleCDR,
                          icon: MdDownload,
                      },
                  ]
                : []),
        ];

        // Conditionally add Update Status action
        if (!approvalHidden) {
            actionItems.push({
                label: 'Update Status',
                onClick: handleStatus,
                icon: MdLoop,
            });
        }

        if (actionItems.length === 1) {
            const action = actionItems[0];
            return (
                <Tooltip label={action.label}>
                    <IconButton
                        icon={
                            <BsThreeDotsVertical
                                fill={darkMode ? 'white' : 'black'}
                            />
                        }
                        aria-label={action.label}
                        variant="ghost"
                        onClick={action.onClick}
                        _hover={{
                            bg: darkMode ? 'whiteAlpha.200' : 'blackAlpha.100',
                        }}
                        _active={{
                            bg: darkMode ? 'whiteAlpha.300' : 'blackAlpha.200',
                        }}
                        _focus={{ boxShadow: 'none' }}
                    />
                </Tooltip>
            );
        }

        // Otherwise render as ActionMenu (which also uses BsThreeDotsVertical)
        return <ActionMenu actionItems={actionItems} />;
    }

    // Table columns for AGGridMain
    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Invoiced By',
                field: 'parentName',
                width: 250,
            },
            { headerName: 'Invoiced To', field: 'entityName' },
            {
                headerName: 'Number',
                field: 'invoiceNumber',
                width: 60,
                minWidth: 60,
                flex: 1,
            },
            {
                headerName: 'Due Date',
                field: 'dueDate',
                cellRenderer: TimeCellRenderer,
                width: 60,
                minWidth: 60,
                flex: 1,
            },
            {
                headerName: 'End Date',
                field: 'endDate',
                cellRenderer: TimeCellRenderer,
                width: 60,
                minWidth: 60,
                flex: 1,
            },
            {
                headerName: 'Total',
                field: 'invoiceTotal',
                width: 60,
                minWidth: 60,
                flex: 1,
                cellRenderer: (params) => (
                    <NegativeSixDP displayType="text" value={params.value} />
                ),
            },
            {
                headerName: 'Line Count',
                field: 'lineCount',
                width: 60,
                minWidth: 60,
                flex: 1,
            },
            {
                headerName: 'Status',
                field: 'invoiceStatus',
                width: 60,
                minWidth: 60,
                flex: 1,
                valueGetter: (params) => {
                    // Return the label for display and filtering
                    return (
                        config.invoices.statusJSON[
                            params.data?.invoiceStatus
                        ] || params.data?.invoiceStatus
                    );
                },
                filterParams: {
                    valueGetter: (params) => {
                        // Use the label for filtering
                        return (
                            config.invoices.statusJSON[
                                params.data?.invoiceStatus
                            ] || params.data?.invoiceStatus
                        );
                    },
                },
            },
            {
                headerName: 'Action',
                lockPosition: 'right',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: ActionsRenderer,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const { columns: agGridColumns, onColumnVisible } =
        useColumnState(baseColumns);

    // Table fetch logic based on scope and invoiceType
    const tableFetch = () => {
        switch (currentScope) {
            case 20:
                if (!currentCompany) return { data: [] };
                break;
            case 40:
                if (!currentPartner) return { data: [] };
                break;
            case 60:
                if (!currentWholesaler) return { data: [] };
                break;
            default:
                break;
        }
        switch (String(invoiceType)) {
            case '2':
                return axios.get('/invoices/', {
                    params: {
                        wholesalerID:
                            currentScope === 60 ? currentWholesaler : null,
                        type: invoiceType,
                        sort: 'endDate',
                        order: 'DESC',
                    },
                });
            case '1':
                return axios.get('/invoices/', {
                    params: {
                        wholesalerID:
                            currentScope === 60 ? currentWholesaler : null,
                        partnerID: currentScope === 40 ? currentPartner : null,
                        type: invoiceType,
                        sort: 'endDate',
                        order: 'DESC',
                    },
                });
            case '0':
                return axios.get('/invoices/', {
                    params: {
                        type: invoiceType,
                        companyID: currentScope === 20 ? currentCompany : null,
                        partnerID: currentScope === 40 ? currentPartner : null,
                        wholesalerID:
                            currentScope === 60 ? currentWholesaler : null,
                        sort: 'endDate',
                        order: 'DESC',
                    },
                });
            default:
                return axios.get('/invoices/', {
                    params: {
                        type: 0,
                        companyID: currentScope === 20 ? currentCompany : null,
                        sort: 'endDate',
                        order: 'DESC',
                    },
                });
        }
    };

    const invoiceQueryEnabled =
        currentScope === 80 ||
        (currentScope === 60 && Boolean(currentWholesaler)) ||
        (currentScope === 40 && Boolean(currentPartner)) ||
        (currentScope === 20 && Boolean(currentCompany));

    // React Query for table data
    const {
        data: tableData = [],
        isFetching,
        error: tableErrorObj,
        refetch,
    } = useQuery({
        queryKey: ['invoiceTable', currentScope, invoiceType],
        queryFn: async () => {
            const res = await tableFetch();
            const data = res.data || [];
            // Sort by endDate descending (newest first)
            return data.sort((a, b) => {
                const dateA = new Date(a.endDate);
                const dateB = new Date(b.endDate);
                return dateB - dateA; // descending order
            });
        },
        enabled: invoiceQueryEnabled,
        keepPreviousData: true,
    });

    // Never lock scope in Redux, but show loader in table during fetch
    // const tableLoading = false;
    const tableError = tableErrorObj ? 'Error loading table data' : '';

    const tableDataWithColumns = useMemo(
        () => ({
            columns: agGridColumns,
            data: tableData,
        }),
        [agGridColumns, tableData],
    );

    // Child view (Invoice Lines)
    if (child) {
        return (
            <>
                <InvoiceLines
                    invoice={child}
                    invoices={tableData}
                    onInvoiceChange={(inv) => setChild(inv)}
                    onBackToInvoices={() => {
                        setChild(null);
                        setModal({
                            show: false,
                            mode: '',
                            data: null,
                            state: null,
                        });
                    }}
                    config={config}
                />
            </>
        );
    }

    // Filter options - match navbar scope list but exclude Global (80)
    const scopeOptions = [];
    for (let value = minimumScope; value <= requiredScope; value += 20) {
        if (value !== 80) {
            // Exclude Global
            scopeOptions.push({
                value,
                label: config.scope.json[value],
            });
        }
    }

    const handleScopeChange = (val) => {
        // Map filter value to invoice type: 20->0 (Company), 40->1 (Partner), 60->2 (Wholesaler)
        const newType = val === 20 ? 0 : val === 40 ? 1 : 2;
        const oldType = invoiceType;

        // Always update invoice type
        dispatch({ type: 'CHANGE_TABLE_INVOICE_TYPE', payload: newType });

        // If filter increases (going up), change scope to match
        // If filter decreases (going down), leave scope as is
        if (newType > oldType) {
            dispatch({ type: 'CHANGE_CURRENT_SCOPE', payload: val });
            // Update ref when we programmatically change scope
            prevScopeRef.current = val;
        }
        //dispatch({ type: 'CHANGE_TABLE_LOADING', payload: false });
    };
    const ScopeDropdown = (
        <HStack pl={5} spacing={2}>
            <HStack
                sx={{
                    '& select': {
                        borderRadius: '6px',
                    },
                }}>
                <span className="text-sm font-medium">Invoice Type:</span>
                <CustomSelect
                    value={invoiceType === 0 ? 20 : invoiceType === 1 ? 40 : 60}
                    onChange={handleScopeChange}
                    width="150px"
                    size="sm"
                    h="32px"
                    isDisabled={false}>
                    {scopeOptions.map(({ label, value }) => (
                        <Option key={label} value={value}>
                            {label}
                        </Option>
                    ))}
                </CustomSelect>
            </HStack>
        </HStack>
    );

    // Check if entity is selected - if not, show alert and don't render table
    if (
        (currentScope === 20 && !currentCompany) ||
        (currentScope === 40 && !currentPartner) ||
        (currentScope === 60 && !currentWholesaler)
    ) {
        return (
            <Alert status="error" style={{ marginTop: 10 }}>
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    // Main table view
    return (
        <>
            <>
                <>
                    <Text
                        sx={{
                            fontSize: '20',
                            fontWeight: '500',
                            marginBottom: '15px',
                        }}>
                        Invoices
                    </Text>
                    <AGGridMain
                        gridRef={tableRef}
                        tableData={tableDataWithColumns}
                        titleName="Invoices"
                        refetch={refetch}
                        extraComponents={ScopeDropdown}
                        isFetching={isFetching}
                        onColumnVisible={onColumnVisible}
                        isSaveButtonEnabled
                        // style={{ width: '100%' }}
                    />
                </>
            </>
            {tableError && (
                <div style={{ color: 'red', margin: 8 }}>{tableError}</div>
            )}

            {/* Modal UI: Edit and Approval modes */}
            {modal.show && modal.mode === 'Edit' && (
                <FieldsDrawer
                    isOpen={modal.show}
                    onClose={() =>
                        setModal({
                            show: false,
                            mode: '',
                            data: null,
                            state: null,
                        })
                    }
                    data={modal.data}
                    state={modal.state}
                    darkMode={darkMode}
                    onChange={() => {
                        setModal({
                            show: false,
                            mode: '',
                            data: null,
                            state: null,
                        });
                        refetch();
                    }}
                />
            )}
            {modal.show && modal.mode === 'Approval' && (
                <ModalApprovalContent
                    isOpen={modal.show}
                    onClose={() =>
                        setModal({
                            show: false,
                            mode: '',
                            data: null,
                            state: null,
                        })
                    }
                    modalData={modal.data}
                    modalState={modal.state}
                    darkMode={darkMode}
                    mode={modal.mode}
                    onChange={async (newState) => {
                        // If submit, call API to update status
                        if (newState.submit) {
                            try {
                                const invoiceId =
                                    modal.data.invoiceId || modal.data.id;
                                // Always use the current value of the dropdown (modal.state.upload)
                                const previousStatus = modal.data.invoiceStatus;
                                const status =
                                    modal.state && modal.state.upload
                                        ? modal.state.upload
                                        : newState.upload;
                                // Endpoint is always /invoice/{invoiceId}/{status}
                                await axios.put(
                                    `/invoice/${invoiceId}/${status}`,
                                );
                                const prevLabel =
                                    config.invoices.statusJSON[
                                        previousStatus
                                    ] ?? previousStatus;
                                const newLabel =
                                    config.invoices.statusJSON[status] ??
                                    status;
                                toast({
                                    title: 'Status Updated',
                                    description: `Invoice status changed from ${prevLabel} to ${newLabel}.`,
                                    status: 'success',
                                    duration: 2000,
                                    isClosable: true,
                                });
                                setModal({
                                    show: false,
                                    mode: '',
                                    data: null,
                                    state: null,
                                });
                                refetch();
                            } catch (err) {
                                toast({
                                    title: 'Status Update Failed',
                                    description:
                                        'Failed to update invoice status.',
                                    status: 'error',
                                    duration: 2000,
                                    isClosable: true,
                                });
                            }
                        } else {
                            // For selection changes, update modal state
                            setModal((prev) => ({
                                ...prev,
                                data: { ...prev.data, ...newState },
                                state: { ...prev.state, ...newState },
                            }));
                        }
                    }}
                />
            )}
        </>
    );
};

export default Invoice;
